'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimelineEvent {
  year: string;
  title: string;
  organization: string;
  description: string;
  achievements: string[];
  type: 'milestone' | 'transition' | 'achievement';
}

interface CareerTimelineProps {
  events: TimelineEvent[];
}

const CareerTimeline: React.FC<CareerTimelineProps> = ({ events }) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'milestone':
        return 'bg-primary';
      case 'transition':
        return 'bg-secondary';
      case 'achievement':
        return 'bg-accent';
      default:
        return 'bg-primary';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'milestone':
        return 'FlagIcon';
      case 'transition':
        return 'ArrowTrendingUpIcon';
      case 'achievement':
        return 'TrophyIcon';
      default:
        return 'StarIcon';
    }
  };

  return (
    <section className="py-16 lg:py-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl mb-4 text-neutral-50">
            Career Journey
          </h2>
          <p className="text-lg max-w-3xl mx-auto text-neutral-50">
            A timeline of growth, leadership, and transformative impact in laboratory operations
          </p>
        </div>
        
        <div className="relative">
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border hidden md:block"></div>
          
          <div className="space-y-8">
            {events.map((event, index) =>
            <div key={index} className="relative">
                <div className="md:flex md:items-start md:space-x-8">
                  <div className="hidden md:flex items-center justify-center w-16 h-16 rounded-full bg-card border-4 border-background shadow-subtle flex-shrink-0 relative z-10">
                    <div className={`w-10 h-10 rounded-full ${getTypeColor(event.type)} flex items-center justify-center`}>
                      <Icon name={getTypeIcon(event.type) as any} size={20} className="text-white" />
                    </div>
                  </div>
                  
                  <div className="flex-1 bg-card rounded-2xl p-6 shadow-subtle hover:shadow-elevated transition-all duration-300 border border-border">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className={`px-3 py-1 ${getTypeColor(event.type)} text-white text-xs font-semibold rounded-full`}>
                            {event.year}
                          </span>
                          <span className="text-xs text-text-secondary font-medium uppercase tracking-wide">
                            {event.type}
                          </span>
                        </div>
                        <h3 className="font-heading font-bold text-xl text-primary mb-1">
                          {event.title}
                        </h3>
                        <p className="text-secondary font-medium">
                          {event.organization}
                        </p>
                      </div>
                      
                      <button
                      onClick={() => toggleExpand(index)}
                      className="ml-4 p-2 hover:bg-muted rounded-lg transition-colors duration-200"
                      aria-label={expandedIndex === index ? 'Collapse details' : 'Expand details'}>

                        <Icon
                        name="ChevronDownIcon"
                        size={24}
                        className={`text-text-secondary transform transition-transform duration-300 ${
                        expandedIndex === index ? 'rotate-180' : ''}`
                        } />

                      </button>
                    </div>
                    
                    <p className="text-text-secondary leading-relaxed mb-4">
                      {event.description}
                    </p>
                    
                    {expandedIndex === index &&
                  <div className="pt-4 border-t border-border space-y-3 animate-fadeIn">
                        <h4 className="font-heading font-semibold text-primary mb-3">
                          Key Achievements:
                        </h4>
                        {event.achievements.map((achievement, idx) =>
                    <div key={idx} className="flex items-start space-x-3">
                            <Icon name="CheckCircleIcon" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                            <p className="text-text-secondary text-sm leading-relaxed">
                              {achievement}
                            </p>
                          </div>
                    )}
                      </div>
                  }
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

};

export default CareerTimeline;